import { MessageType } from '@chessclub/proto-ts/index.websocket_gateway'
import { action, Action, thunk, Thunk } from 'easy-peasy'

import * as Sentry from '@sentry/react'
import axios from 'axios'
import { analyticsManager } from '../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { StoreModel } from '../../store/store'
import { claimMission, getMissions } from './MissionsAPI'
import { Achievement, Mission, Reward, Summary } from './missionsTypes'

// Define the WebSocket model interface
export interface MissionsServiceModel {
    nextResetAt: string // Optional, as it may not be present in all summaries
    nextMultiplierExpiresAt: string
    guestEnabled: boolean
    missions?: Mission[]
    rewards?: Reward[]
    achievements?: Achievement[]
    isCompletedMissions: boolean
    completedMission: Mission | undefined
    claimedMission: Reward | undefined
    loadingSummary: boolean
    summaryError: boolean
    upgradingMission: Mission | undefined
    getMissionsAbortController: AbortController | undefined

    // Actions
    setMissions: Action<MissionsServiceModel, Mission[]>
    setUpgradingMission: Action<MissionsServiceModel, Mission | undefined>
    setRewards: Action<MissionsServiceModel, Reward[]>
    setAchievements: Action<MissionsServiceModel, Achievement[]>
    setIsCompletedMissions: Action<MissionsServiceModel, boolean>
    setNextResetAt: Action<MissionsServiceModel, string>
    setNextMultiplierExpiresAt: Action<MissionsServiceModel, string>
    setGuestEnabled: Action<MissionsServiceModel, boolean>

    setCompletedMission: Action<MissionsServiceModel, Mission | undefined>
    setClaimedMission: Action<MissionsServiceModel, Reward | undefined>
    setLoadingSummary: Action<MissionsServiceModel, boolean>
    setSummaryError: Action<MissionsServiceModel, boolean>
    setGetMissionsAbortController: Action<MissionsServiceModel, AbortController | undefined>

    // Thunks
    getMissions: Thunk<MissionsServiceModel, number | undefined, undefined, StoreModel>
    establishListener: Thunk<MissionsServiceModel, undefined, undefined, StoreModel>
    claimMission: Thunk<MissionsServiceModel, Mission, undefined, StoreModel>
}

// WebSocket model implementation
export const MissionsServiceModel: MissionsServiceModel = {
    // State
    nextResetAt: '',
    nextMultiplierExpiresAt: '',
    guestEnabled: false,
    missions: undefined,
    rewards: undefined,
    achievements: undefined,
    isCompletedMissions: false,
    completedMission: undefined,
    claimedMission: undefined,
    loadingSummary: true,
    summaryError: false,
    upgradingMission: undefined,
    getMissionsAbortController: undefined,

    // Actions

    setLoadingSummary: action((state, loading) => {
        state.loadingSummary = loading
    }),

    setSummaryError: action((state, failed) => {
        state.summaryError = failed
    }),

    setMissions: action((state, missions) => {
        state.missions = missions
        // Flatten all achievements from all rewards for convenience
        state.achievements = state.rewards ? state.rewards.flatMap((reward) => reward.achievements || []) : []
    }),

    setRewards: action((state, rewards) => {
        state.rewards = rewards
        // Flatten all achievements from all rewards for convenience
        state.achievements = rewards ? rewards.flatMap((reward) => reward.achievements || []) : []
    }),

    setAchievements: action((state, achievements) => {
        state.achievements = achievements
    }),

    setNextResetAt: action((state, nextResetAt) => {
        state.nextResetAt = nextResetAt
    }),

    setNextMultiplierExpiresAt: action((state, nextMultiplierExpiresAt) => {
        state.nextMultiplierExpiresAt = nextMultiplierExpiresAt
    }),

    setIsCompletedMissions: action((state, isCompleted) => {
        state.isCompletedMissions = isCompleted
    }),

    setCompletedMission: action((state, mission) => {
        state.completedMission = mission
    }),

    setClaimedMission: action((state, mission) => {
        state.claimedMission = mission
    }),

    setUpgradingMission: action((state, mission) => {
        state.upgradingMission = mission
    }),

    setGuestEnabled: action((state, enabled) => {
        state.guestEnabled = enabled
    }),

    setGetMissionsAbortController: action((state, controller) => {
        state.getMissionsAbortController = controller
    }),

    // Thunks

    getMissions: thunk(async (actions, newEarnedId, { getStoreState }) => {
        // Abort previous getMissions request if it exists
        actions.setLoadingSummary(true)
        actions.setSummaryError(false)
        const previousController = getStoreState().missionsService.getMissionsAbortController
        if (previousController) {
            previousController.abort()
        }

        // Create new AbortController for this request
        const abortController = new AbortController()
        actions.setGetMissionsAbortController(abortController)

        try {
            const data: Summary | undefined = await getMissions(abortController.signal)

            if (!data) return

            actions.setNextResetAt(data.nextResetAt)
            actions.setNextMultiplierExpiresAt(data.nextMultiplierExpiresAt)
            actions.setMissions(data.missions || [])
            actions.setGuestEnabled(data.guestEnabled)
            actions.setRewards(
                newEarnedId
                    ? data.rewards.map((reward) => {
                          return {
                              ...reward,
                              achievements:
                                  reward.achievements?.map((achievement) => ({
                                      ...achievement,
                                      isNew: achievement.missionId === newEarnedId,
                                  })) || null,
                          }
                      })
                    : data.rewards,
            )
            actions.setIsCompletedMissions(data.missions.some((mission) => mission.threshold === mission.progress))
        } catch (error) {
            // Don't log abort errors to Sentry
            if (axios.isCancel(error)) {
                return
            }

            actions.setSummaryError(true)
            if (!axios.isAxiosError(error)) {
                Sentry.captureException(error)
                return
            }
            // no response (offline / browser abort / timeout) and 401 are client-side conditions, not bugs
            const status = error.response?.status
            if (!status || status === 401) {
                Sentry.addBreadcrumb({
                    category: 'missions',
                    level: 'warning',
                    message: 'GET /summary failed',
                    data: { status, code: error.code },
                })
            } else if (status >= 500) {
                Sentry.captureMessage('Missions summary failed: HTTP 5xx', {
                    level: 'warning',
                    fingerprint: ['missions-summary', '5xx'],
                    tags: { endpoint: 'mission-service/summary', http_status: status },
                })
            } else {
                Sentry.captureException(error, {
                    fingerprint: ['missions-summary', String(status)],
                    tags: { endpoint: 'mission-service/summary', http_status: status },
                })
            }
        } finally {
            // a superseded (aborted) call must not end the newer call's loading state
            if (getStoreState().missionsService.getMissionsAbortController === abortController) {
                actions.setGetMissionsAbortController(undefined)
                actions.setLoadingSummary(false)
            }
        }
    }),

    claimMission: thunk(async (actions, mission, { getStoreState, getStoreActions }) => {
        // Abort any ongoing getMissions request before claiming
        const previousController = getStoreState().missionsService.getMissionsAbortController
        if (previousController) {
            previousController.abort()
            actions.setGetMissionsAbortController(undefined)
        }

        mission.upgradesTier && actions.setUpgradingMission(mission)
        let data: Reward
        try {
            data = await claimMission(mission.id)
        } catch {
            // not claimed (or unknown): no reward animation / analytics; resync the list
            actions.setUpgradingMission(undefined)
            getStoreActions().setSnackbarMessage("Couldn't claim the reward. Please try again.")
            actions.getMissions()
            return
        }

        // Dispatch missionClaimed analytics event
        analyticsManager.dispatchEvent('missionClaimed', {
            missionType: mission.repeatable ? 'repeatable' : 'one-time',
            missionName: mission.title,
            missionId: mission.id,
        })

        actions.setClaimedMission(data)
        actions.getMissions(mission.id)
    }),

    establishListener: thunk((actions, _, { getStoreState, getStoreActions }) => {
        const { ws } = getStoreActions()
        const { isConnected } = getStoreState().ws
        if (isConnected) {
            ws.addListener({
                messageType: MessageType.MISSION_COMPLETED,
                callback: (message) => {
                    const missions = getStoreState().missionsService.missions || []
                    const updatedMissions = missions.map((mission) => {
                        if (mission.id === message.id) {
                            // Updated mission with new progress
                            return message
                        }
                        return mission
                    })

                    actions.setMissions(updatedMissions)
                    actions.setCompletedMission(message)

                    // Dispatch missionCompleted analytics event
                    analyticsManager.dispatchEvent('missionCompleted', {
                        missionType: message.repeatable ? 'repeatable' : 'one-time',
                        missionName: message.title,
                        missionId: message.id,
                    })
                },
            })
        }
    }),
}
